import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type {
  FidelityCustomerDiscountCard,
  FidelityRewardGrant,
  FidelityRewardRule,
} from '../types/fidelity.types';

type FetchPage = (
  args: FetchArgs,
) => ReturnType<BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>>;

/** Reward endpoints can return either a list or a Spring page. Never drop pending grants on later pages. */
export async function fetchFidelityRewardList<T>(
  url: string,
  fetchPage: FetchPage,
  normalize: (raw: unknown) => T,
): Promise<{ data: T[] } | { error: FetchBaseQueryError }> {
  const rows: T[] = [];
  try {
    for (let page = 0; ; page++) {
      const response = await fetchPage({
        url: `${url}&page=${String(page)}&size=100`,
        method: 'GET',
      });
      if (response.error) return { error: response.error };
      const value = normalizePro20Response(response.data);
      if (Array.isArray(value))
        return { data: [...rows, ...value.map(normalize)] };
      const result = record(value);
      if (!Array.isArray(result.content))
        throw Error('Formato della lista premi non valido.');
      rows.push(...result.content.map(normalize));
      const totalPages = Number(result.totalPages);
      if (
        result.last === true ||
        (Number.isFinite(totalPages) && page + 1 >= totalPages)
      ) {
        return { data: rows };
      }
      if (
        result.content.length === 0 ||
        !Number.isSafeInteger(totalPages) ||
        page + 1 >= 1000
      ) {
        throw Error('La lista premi ricevuta è incompleta. Riprova.');
      }
    }
  } catch (error) {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error:
          error instanceof Error
            ? error.message
            : 'Impossibile caricare i premi.',
      },
    };
  }
}

function record(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw))
    throw Error('Risposta premi non valida.');
  return raw as Record<string, unknown>;
}

function id(value: unknown): string | null {
  return typeof value === 'number' || typeof value === 'string'
    ? String(value)
    : null;
}

export function normalizeFidelityRewardRule(raw: unknown): FidelityRewardRule {
  const row = record(raw);
  const rewardRuleId = id(row.rewardRuleId);
  if (!rewardRuleId)
    throw Error('Identificativo della regola premio mancante.');
  return {
    ...row,
    rewardRuleId,
    thresholdCode: id(row.thresholdCode),
  } as FidelityRewardRule;
}

export function normalizeFidelityRewardGrant(
  raw: unknown,
): FidelityRewardGrant {
  const row = record(raw);
  const grantId = id(row.grantId);
  if (!grantId) throw Error('Identificativo del premio assegnato mancante.');
  return {
    ...row,
    grantId,
    grantedAt: typeof row.grantedAt === 'string' ? row.grantedAt : '',
    rewardRuleId: id(row.rewardRuleId) ?? '',
    thresholdCode: id(row.thresholdCode),
    ...(row.customerDiscountCard
      ? {
          customerDiscountCard: normalizeFidelityCustomerDiscount(
            row.customerDiscountCard,
          ),
        }
      : {}),
  } as FidelityRewardGrant;
}

export function normalizeFidelityCustomerDiscount(
  raw: unknown,
): FidelityCustomerDiscountCard {
  const row = record(raw);
  return { ...row, grantId: id(row.grantId) } as FidelityCustomerDiscountCard;
}
